import React, { useState } from "react";
import { FaEye, FaEdit, FaTrash, FaDownload } from "react-icons/fa";
import DatePicker from "react-datepicker";
import styles from "../../assets/scss/Ledger/Ledgertable.module.scss";
import PopupWrapper from "../../Components/Common/PopupWrapper";
import AddExpensePopup from "./AddExpensePopup";
import DeleteConfirmModalLogout from "../../Components/Common/DeleteConfirmModalLogout";
import Pagination from "../../Components/Common/Pagination";
import { useGetExpensesQuery, useDeleteExpenseMutation } from "../../slices/admin/admin";
import { toast } from "react-toastify";
import Select from "react-select";
import { capitalize } from "../../utils/capitalize";

const ExpenseTable = () => {
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [isPopupOpen, setIsPopupOpen] = useState({ open: false, mode: "add", data: null });
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedExpenseId, setSelectedExpenseId] = useState(null);

  const offset = currentPage;

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const { data, error, isLoading, refetch } = useGetExpensesQuery({
    search,
    fromDate: fromDate ? formatDate(fromDate) : "",
    toDate: toDate ? formatDate(toDate) : "",
    offset,
    limit,
  });

  const [deleteExpense, { isLoading: isDeleting }] = useDeleteExpenseMutation();

  const clearFilters = () => {
    setSearch("");
    setFromDate(null);
    setToDate(null);
    setCurrentPage(1);
  };

  const handleDeleteClick = (id) => {
    setSelectedExpenseId(id);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedExpenseId) return;
    try {
      await deleteExpense(selectedExpenseId).unwrap();
      toast.success("Expense deleted successfully!");
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || "Failed to delete expense");
    } finally {
      setDeleteModalOpen(false);
      setSelectedExpenseId(null);
    }
  };

  const reports = data?.result || [];
  const total = data?.totalRecords || data?.total || reports.length;
  const totalPages = Math.ceil(total / limit) || (reports.length > 0 ? 1 : 0);

  return (
    <div className={styles.ExpenseContainer}>
      <h2 className={styles.title}>Expense</h2>

      {/* Filters */}
      <div className={styles.filterBar}>
        <input
          type="text"
          placeholder="Search..."
          className={styles.searchInput}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <DatePicker
          selected={fromDate}
          onChange={setFromDate}
          dateFormat="dd.MM.yyyy"
          placeholderText="From Date"
          maxDate={new Date()}
          isClearable
          className={styles.inputss}
        />
        <DatePicker
          selected={toDate}
          onChange={setToDate}
          dateFormat="dd.MM.yyyy"
          placeholderText="To Date"
          maxDate={new Date()}
          isClearable
          className={styles.inputss}
        />
        <button className={styles.clearBtn} onClick={clearFilters}>
          Clear All
        </button>
        <button
          className={styles.addBtn}
          onClick={() => setIsPopupOpen({ open: true, mode: "add", data: null })}
        >
          + Add Expense
        </button>
      </div>

      {/* Table */}
      <div className={styles.tableWrapper}>
        <table>
          <thead>
            <tr>
              <th>Expense ID</th>
              <th>Expense Date</th>
              <th>Category</th>
              <th>Payment Status</th>
              <th>Payment Type</th>
              <th>Amount</th>
              <th>Description</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan="8">Loading...</td></tr>
            ) : error ? (
              <tr><td colSpan="8">Error fetching data</td></tr>
            ) : data?.result.length > 0 ? (
              data.result.map((item) => (
                <tr key={item._id}>
                  <td>{item.expenseId}</td>
                  <td>{new Date(item.paymentDate).toLocaleDateString()}</td>
                  <td>{item.category ? capitalize(item.category) : '-'}</td>
                  <td>
                    <span className={`${styles.status} ${item.paymentStatus?.toLowerCase() === "paid"
                        ? styles.statusApproved
                        : item.paymentStatus?.toLowerCase() === "pending"
                          ? styles.statusRejected
                          : styles.statusDefault
                      }`}>
                      {item.paymentStatus ? capitalize(item.paymentStatus) : '-'}
                    </span>
                  </td>
                  <td>{item.paymentMethod ? capitalize(item.paymentMethod) : "-"}</td>
                  <td className="notranslate">{item.amount}</td>
                  <td>{item.description ? capitalize(item.description) : '-'}</td>
                  <td>
                    <div className={styles.actions}>
                      <FaEye
                        className={styles.icon}
                        onClick={() => setIsPopupOpen({ open: true, mode: "view", data: item })}
                      />
                      <FaEdit
                        className={styles.icon}
                        onClick={() => setIsPopupOpen({ open: true, mode: "edit", data: item })}
                      />
                      <FaTrash
                        className={styles.icon}
                        onClick={() => handleDeleteClick(item._id)}
                      />
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr><td className={styles.noData} colSpan="8">No expense records found</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Total Section */}
      <div className={styles.totalAmountSection}>
        <div className={styles.totalContent}>
          <div className={styles.totalRow}>
            <span className={styles.label}>Total Amount : </span>
            <span className={styles.amountValue}>
              ₹ {data ? data.totalExpense : 0}
            </span>
          </div>
          {/* <button className={styles.downloadBtn}>
            Download <FaDownload />
          </button> */}
        </div>
      </div>

      {/* Pagination */}
      <div className={styles.paginationWrapper}>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
        <select
          className={styles.inputsbtn}
          value={limit}
          onChange={(e) => {
            setLimit(Number(e.target.value));
            setCurrentPage(1);
          }}
        >
          <option value={10}>10 rows</option>
          <option value={25}>25 rows</option>
          <option value={50}>50 rows</option>
        </select>
      </div>

      {/* Popup */}
      {isPopupOpen.open && (
        <PopupWrapper onClose={() => setIsPopupOpen({ open: false, mode: "add", data: null })}>
          <AddExpensePopup
            mode={isPopupOpen.mode}
            data={isPopupOpen.data}
            onClose={() => setIsPopupOpen({ open: false, mode: "add", data: null })}
            onSave={refetch}
          />
        </PopupWrapper>
      )}

      <DeleteConfirmModalLogout
        isOpen={deleteModalOpen}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteModalOpen(false)}
        title="Are You Sure?"
        heading="Delete Confirmation"
        content="Do you really want to delete this expense? This action cannot be undone."
      />
    </div>
  );
};

export default ExpenseTable;

